export type ProgressCallback = (loadedBytes: number, totalBytes: number | null) => void;

/**
 * Read a Response body to a Uint8Array, calling `onProgress` as chunks arrive.
 * Falls back to a single `arrayBuffer()` read if the body stream is unavailable.
 */
export const readResponseBytesWithProgress = async (
  response: Response,
  onProgress?: ProgressCallback,
): Promise<Uint8Array> => {
  const contentLength = response.headers.get('content-length');
  const totalBytes = typeof contentLength === 'string' ? Number(contentLength) || null : null;

  if (!response.body) {
    const arrayBuffer = await response.arrayBuffer();
    const bytes = new Uint8Array(arrayBuffer);
    onProgress?.(bytes.byteLength, totalBytes ?? bytes.byteLength);
    return bytes;
  }

  const reader = response.body.getReader();
  const chunks: Uint8Array[] = [];
  let loadedBytes = 0;

  while (true) {
    const { done, value } = await reader.read();
    if (done) {
      break;
    }
    if (value) {
      chunks.push(value);
      loadedBytes += value.length;
      onProgress?.(loadedBytes, totalBytes);
    }
  }

  const merged = new Uint8Array(loadedBytes);
  let offset = 0;
  for (const chunk of chunks) {
    merged.set(chunk, offset);
    offset += chunk.length;
  }

  onProgress?.(loadedBytes, totalBytes ?? loadedBytes);
  return merged;
};
